import React, { useState, useEffect, useCallback } from "react";
import {
    BrowserRouter as Router,
    Route,
    Routes,
    Navigate,
    useLocation,
} from "react-router-dom";
import Login from "./pages/Login/Login";
import VerifyMobile from "./pages/Login/VerifyMobile";
import Home from "./pages/Home/Home";
import Training from "./pages/Training/Training";
import Dashboard from "./components/Dashboard/Dashboard";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import Updates from "./pages/Updates/Updates";
import { getUserId, bootstrapSwitchAuthFromUrl } from "./utils/cookieUtils";

function AnalyticsTracker() {
    const location = useLocation();

    useEffect(() => {
        if (window.gtag) {
            window.gtag("config", "G-TGGXWX5M1P", {
                page_path: location.pathname,
            });
        }
    }, [location]);

    return null;
}

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(false);

    const checkAuth = useCallback(() => {
        // Persist switch params into cookies/token BEFORE child routes can redirect to login
        const { didBootstrap, userId, token } = bootstrapSwitchAuthFromUrl();

        if (didBootstrap && userId) {
            // Strip switch params only after token is persisted (cookie + memory + sessionStorage)
            if (window.location.search && (token || !new URLSearchParams(window.location.search).get('token'))) {
                window.history.replaceState(
                    {},
                    document.title,
                    window.location.pathname + window.location.hash
                );
            }
            setIsAuthenticated(true);
            setLoading(false);
            return;
        }

        const cookieUserId = getUserId();
        const isAuth = !!cookieUserId;
        setIsAuthenticated((prev) => (prev !== isAuth ? isAuth : prev));
        setLoading(false);
    }, []);

    useEffect(() => {
        checkAuth();

        window.addEventListener("storage", checkAuth);

        let timeoutId;
        const handleAuthUpdate = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                checkAuth();
            }, 10);
        };
        window.addEventListener("authUpdated", handleAuthUpdate);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("storage", checkAuth);
            window.removeEventListener("authUpdated", handleAuthUpdate);
        };
    }, [checkAuth]);

    useEffect(() => {
        window.addEventListener("beforeinstallprompt", (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        });

        window.addEventListener("appinstalled", () => {
            console.log("PWA installed");
            setIsInstalled(true);
        });

        return () => {
            window.removeEventListener("beforeinstallprompt", () => {});
            window.removeEventListener("appinstalled", () => {});
        };
    }, []);

    if (loading) return <div>Loading...</div>;

    return (
        <Router>
            <AnalyticsTracker />

            <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/verify-mobile" element={<VerifyMobile />} />

                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <Dashboard />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Navigate to="home" />} />
                    <Route path="home" element={<Home />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="updates" element={<Updates />} />
                    <Route path="training" element={<Training />} />
                </Route>

                <Route path="*" element={<Login />} />
            </Routes>
        </Router>
    );
}

export default App;
